import React, { useState, useEffect, useContext } from 'react';
import { auth, googleProvider } from '../firebase';
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import { UserContext } from '../context/UserContext';
import Alert from '@mui/material/Alert';
import { debugGoogleAuth, logAuthConfig } from '../utils/debugAuth';

const AuthDisplay = () => {
  const { user, setUser } = useContext(UserContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser({
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || currentUser.email?.split('@')[0],
          photoURL: currentUser.photoURL,
          isGuest: false
        });
      } else if (user && !user.isGuest) {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, [setUser]);

  const handleGoogleSignIn = async () => {
    try {
      // Log config before attempting sign-in
      logAuthConfig();
      
      const result = await signInWithPopup(auth, googleProvider);
      setUser({
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        isGuest: false
      });
      console.log("User signed in with Google: ", result.user);
    } catch (error) {
      console.error("Error during Google sign-in: ", error);
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('Sign-in popup closed by user.');
      }
      // Use debug function for more detailed error info
      debugGoogleAuth().catch(err => console.error('Debug also failed:', err));
    }
  };

  const handleEmailAuth = async () => {
    setErrorMessage(''); // Clear any previous errors
    
    try {
      if (isSignUp) {
        // Sign up with email/password
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        setUser({
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.email.split('@')[0],
          isGuest: false
        });
        console.log("User signed up with email: ", userCredential.user);
      } else {
        // Sign in with email/password
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        setUser({
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.email.split('@')[0],
          isGuest: false
        });
        console.log("User signed in with email: ", userCredential.user);
      }
      setDialogOpen(false);
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error("Error during email auth: ", error);
      setErrorMessage(
        error.code === 'auth/email-already-in-use' ? 'Email already in use. Try signing in instead.' :
        error.code === 'auth/invalid-credential' ? 'Invalid email or password.' :
        error.code === 'auth/weak-password' ? 'Password should be at least 6 characters.' :
        'Authentication error. Please try again.'
      );
    }
  };

  const handleSignOut = async () => {
    try {
      // Only call Firebase signOut - don't manipulate the user state directly
      // The UserContext's onAuthStateChanged listener will handle updating the state
      await signOut(auth);
      console.log("User signed out - waiting for auth state change");
    } catch (error) {
      console.error("Error during sign-out: ", error);
    }
  };

  const handleContinueAsGuest = () => {
    setUser({ isGuest: true, displayName: "Guest", uid: 'guest-' + Date.now() });
    console.log("Continuing as guest");
    setAnchorEl(null);
  };

  const openAuthMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeAuthMenu = () => {
    setAnchorEl(null);
  };

  const openEmailDialog = (signUp) => {
    setIsSignUp(signUp);
    setDialogOpen(true);
    setErrorMessage('');
    setAnchorEl(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEmail('');
    setPassword('');
    setErrorMessage('');
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {user && !user.isGuest ? (
          <>
            {user.photoURL ? (
              <Avatar alt={user.displayName || 'User'} src={user.photoURL} sx={{ width: 32, height: 32 }} />
            ) : (
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a2e46' }}>
                {(user.displayName?.charAt(0) || user.email?.charAt(0) || 'U').toUpperCase()}
              </Avatar>
            )}
            <Typography variant="subtitle2" sx={{ mr: 1 }}>{user.displayName || user.email}</Typography>
            <Button variant="outlined" size="small" onClick={handleSignOut}>Sign Out</Button>
          </>
        ) : user && user.isGuest ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
            <Typography variant="subtitle2" sx={{ mr: 1 }}>Welcome, Guest</Typography>
            <Typography variant="caption" sx={{ fontStyle: 'italic', flexGrow: 1, mr: 1 }}>
              (Note: Data will not be saved. Sign in to save progress.)
            </Typography>
            <Button variant="contained" size="small" onClick={openAuthMenu} startIcon={<AccountCircleIcon />}>
              Sign In
            </Button>
          </Box>
        ) : (
          <Button 
            variant="contained" 
            size="small" 
            onClick={openAuthMenu} 
            startIcon={<AccountCircleIcon />}
            sx={{ bgcolor: '#1a2e46', ':hover': { bgcolor: '#34517a' } }}
          >
            Sign In
          </Button>
        )}
      </Box>

      {/* Authentication Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeAuthMenu}
        MenuListProps={{
          'aria-labelledby': 'auth-button',
        }}
      >
        <MenuItem onClick={handleGoogleSignIn} dense>
          <GoogleIcon fontSize="small" sx={{ mr: 1 }} /> Sign in with Google
        </MenuItem>
        <MenuItem onClick={() => openEmailDialog(false)} dense>
          <EmailIcon fontSize="small" sx={{ mr: 1 }} /> Sign in with Email
        </MenuItem>
        <MenuItem onClick={handleContinueAsGuest} dense>
          Continue as Guest
        </MenuItem>
      </Menu>

      {/* Email Sign In/Up Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{isSignUp ? 'Sign Up' : 'Sign In'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isSignUp 
              ? 'Create an account to save your FERS projections and scenarios.'
              : 'Sign in to access your saved FERS projections and scenarios.'}
          </DialogContentText>
          
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {errorMessage}
            </Alert>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={() => setIsSignUp(!isSignUp)}>
            {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
          </Button>
          <Button 
            onClick={handleEmailAuth} 
            variant="contained"
            color="primary"
            disabled={!email || !password}
          >
            {isSignUp ? 'Create Account' : 'Sign In'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AuthDisplay;
